/**
 * Transforms flow data from the contribution creation flow into
 * the format expected by the contributions API.
 *
 * This function extracts data from the flow steps and constructs
 * the appropriate foreign key references based on the entity type:
 * - Regular events → use eventId
 * - Festival/Series → use eventSeriesId
 * - Exhibitions → use exhibitionEventId
 * - Specific representations → use eventOfferId
 *
 * Note: The backend (lvc-functions) handles the "LVC-PROD-ID-{numericId}"
 * string formatting for Supabase compatibility.
 *
 * @param flowData - The raw flow data from the contribution creation flow
 * @returns Formatted contribution data with numeric IDs
 *
 * @example
 * // Input:
 * {
 *   createContributionSearchOrAdd: {
 *     contributorData: { id: 13221, contributorName: "Bootlegger" }
 *   },
 *   createContributionSelectType: {
 *     contributionType: "Artiste"
 *   },
 *   createContributionAddEvents: {
 *     addedEvents: [{
 *       eventData: { id: 9964, type: "Event" },
 *       addToParentEvent: false,
 *       selectedOffers: [{ id: 1515772 }]
 *     }]
 *   }
 * }
 *
 * // Output:
 * {
 *   contributorId: 13221,
 *   contributionType: "Artiste",
 *   contributions: [
 *     { eventOfferId: 1515772 }
 *   ]
 * }
 */
export function transformFlowDataToContributionFormat(flowData: any): {
  contributorId: number
  contributorNameFr: string
  contributorNameEn: string
  contributionType: string
  contributions: Array<{
    eventId?: number
    eventOfferId?: number
    eventSeriesId?: number
    exhibitionEventId?: number
  }>
} {
  const contributorData = flowData.createContributionSearchOrAdd?.contributorData
  const contributionType = flowData.createContributionSelectType?.contributionType
  const addedEvents = flowData.createContributionAddEvents?.addedEvents || []

  const contributions: any[] = []

  for (const event of addedEvents) {
    if (event.addToParentEvent) {
      // Add to entire event/series/exhibition
      const entityId = event.eventData?.id

      if (event.eventData?.type === 'Festival') {
        contributions.push({ eventSeriesId: entityId })
      } else if (event.eventData?.type === 'Exhibition') {
        contributions.push({ exhibitionEventId: entityId })
      } else {
        contributions.push({ eventId: entityId })
      }
    } else {
      // Add to specific offers
      for (const offer of event.selectedOffers || []) {
        contributions.push({ eventOfferId: offer.id })
      }
    }
  }

  return {
    contributorId: contributorData?.id,
    contributorNameFr: contributorData?.nameFr || '',
    contributorNameEn: contributorData?.nameEn || '',
    contributionType,
    contributions,
  }
}
